import { CurrentUser } from "@app/api/auth";
import { UserType } from "@core/enums/user-type";
import { TPagination } from "@custom-types/sorting";

import { CompanyUserModel } from "./user-company.model";
import { UserExtended } from "./user-extended.model";

export type TAllClientsPaginated = TPagination & {
  data: Array<TAllClients>;
};

export type TAllClients = UserExtended & CompanyUserModel;

export interface UserModel {
  email: string;
  id: string;
  rights: Array<number>;
  /**
   * The roles the API reports for the signed-in user.
   *
   * They arrived on every /auth/me response and were read once, to decide
   * UserType, then thrown away - so the shell had a name to show and nothing
   * to say about what that person is.
   */
  roles?: Array<string>;
  userName: string;
  userType: UserType;
}

export class User implements UserModel {
  email = "";
  id = "";
  // Optional, deliberately. UserExtended is `Omit<User, "rights">`, so a
  // required field here becomes a required field on every model that embeds a
  // user - the account owner, a transfer's two sides, a message's recipient -
  // none of which the API sends roles for.
  roles?: Array<string>;
  rights: Array<number> = [];
  userName = "";
  userType = UserType.private;

  constructor(dto?: UserModel) {
    if (!dto) {
      return;
    }

    this.email = dto.email;
    this.id = dto.id;
    this.rights = dto.rights;
    this.roles = dto.roles;
    this.userName = dto.userName;
    this.userType = dto.userType;
  }

  static jsonSerialize(user: User): UserModel {
    return { ...user };
  }

  static parseDto(dto: UserModel): User {
    return new User(dto);
  }

  static parseCurrentUser(dto: CurrentUser): User {
    return new User({
      id: dto.id,
      email: dto.email ?? "",
      rights: dto.rights,
      // Group names first: the identity carries no role claims, so `roles` is
      // empty for employees and the group (Management, HR...) is what people
      // call their role. Older backends don't send groups; roles stays the
      // fallback.
      roles: [...(dto.groups ?? []), ...(dto.roles ?? [])],
      userName: dto.username,
      userType: User.mapUserType(dto.roles),
    });
  }

  private static mapUserType(roles: Array<string>): UserType {
    // Replace these role names with the exact backend role values.
    if (roles.includes("Company")) {
      return UserType.company;
    }

    return UserType.private;
  }
}

/**
 * The role to show beside a user's name.
 *
 * A free function rather than a getter on User, because `UserExtended` is
 * `Omit<User, "rights">` - anything added to the class becomes a required
 * member of every model that embeds a user: the account owner, both sides of
 * an internal transfer, a message's recipient. None of those have a role.
 *
 * An employee can hold several; the first is the one the back office calls
 * them by. Identity-provider role names arrive exactly as stored, so
 * underscores and camelCase are spaced out before being shown.
 */
export function primaryRoleOf(user: User | undefined | null): string {
  const role = (user?.roles ?? []).find(
    (value: string): boolean => value.trim().length > 0
  );

  if (!role) {
    return "";
  }

  return role
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
}
